import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { View, Text, Pressable, ScrollView, Platform, NativeSyntheticEvent, NativeScrollEvent } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { Image } from 'expo-image'
import { useTheme } from '../../../src/theme/useTheme'
import { getNotificationDropdownStyles } from './NotificationDropdown.styles'
import {
  buildDropdownList,
  formatRelativeTime,
  formatStartsIn,
} from '../../../src/notifications/helpers/notificationsUi.helper'
import { NOTIFICATION_ACTION_LABELS } from '../../../src/notifications/constants/notifications.constants'
import { DEFAULT_GROUP_IMAGE_SOURCE } from '../../../constants/assets'
import {
  webImageAltAvatarLabel,
  webImageAltGroupCover,
} from '../../../src/shared/helpers/imageWebAlt.helper'
import type { Notification } from '../../../src/notifications/types/notifications.types'
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types'
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types'
import { AvatarDisplay } from '../../avatars/AvatarDisplay/AvatarDisplay'

interface NotificationDropdownProps {
  notifications: Notification[]
  unreadCount: number
  onClose: () => void
  onMarkRead: (id: string) => void
  onMarkAllRead: () => void
  onNotificationPress?: (notification: Notification) => void
  topOffset: number
  testID?: string
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount: _unreadCount,
  onClose,
  onMarkRead,
  onMarkAllRead,
  onNotificationPress,
  topOffset,
  testID = 'notification-dropdown',
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getNotificationDropdownStyles(theme, { topOffset }), [theme, topOffset])
  const [displayList, setDisplayList] = useState<Notification[]>([])
  const [isAtBottom, setIsAtBottom] = useState(false)
  const snapshotTakenRef = useRef(false)

  useEffect(() => {
    if (notifications.length > 0 && !snapshotTakenRef.current) {
      snapshotTakenRef.current = true
      setDisplayList(notifications.map((n) => ({ ...n })))
      onMarkAllRead()
    }
  }, [notifications, onMarkAllRead])

  const { visibleItems } = useMemo(
    () => buildDropdownList(displayList.length > 0 ? displayList : notifications),
    [displayList, notifications],
  )

  const handleScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent
    setIsAtBottom(contentOffset.y + layoutMeasurement.height >= contentSize.height - 20)
  }, [])

  const handleItemPress = useCallback(
    (item: Notification) => {
      if (!item.read) onMarkRead(item.id)
      onNotificationPress?.(item)
    },
    [onMarkRead, onNotificationPress],
  )

  return (
    <View style={styles.overlay} testID={testID}>
      <Pressable
        style={styles.backdropPressable}
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Dismiss notifications"
      />

      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Notifications</Text>
        </View>

        {visibleItems.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No notifications yet</Text>
          </View>
        ) : (
          <View style={styles.listWrapper}>
          <ScrollView
            style={[
              styles.list,
              Platform.OS === 'web' ? styles.listWebScroll : undefined,
            ]}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            {visibleItems.map((item, index) => {
              const isLast = index === visibleItems.length - 1
              const isUnread = !item.read
              const data = item.data as {
                actorName?: string
                actorAvatarId?: string
                actorAvatarConfig?: EquippedMap | null
                startsAt?: string
                isGroup?: boolean
                groupName?: string
              } | undefined
              const isGroup = Boolean(data?.isGroup)
              const actorAvatarId = data?.actorAvatarId as DisplayAvatarId | undefined
              const actorAvatarConfig = data?.actorAvatarConfig ?? null
              const displayName = isGroup
                ? (data?.groupName ?? item.title ?? 'Group')
                : (data?.actorName ?? item.title ?? 'Someone')
              const actionLabel = NOTIFICATION_ACTION_LABELS[item.type] ?? item.type
              const startsAt = typeof data?.startsAt === 'string' ? data.startsAt : undefined
              const messageLine = item.message?.trim() || null
              const startsInLine = startsAt ? formatStartsIn(startsAt) : null
              const contextLines = [messageLine, startsInLine].filter(Boolean)
              const contextMessage = contextLines.length > 0 ? contextLines.join(' · ') : null
              return (
                <View key={item.id}>
                  <Pressable
                    style={[
                      styles.itemContainer,
                      isUnread ? styles.itemContainerUnread : styles.itemContainerRead,
                    ]}
                    onPress={() => handleItemPress(item)}
                    testID={`${testID}-item-${item.id}`}
                  >
                    {isUnread && <View style={styles.itemAccentBar} />}
                    <View style={styles.itemRow}>
                      <View style={styles.avatarWrapper}>
                        {isGroup ? (
                          <Image
                            source={DEFAULT_GROUP_IMAGE_SOURCE}
                            style={styles.groupImage}
                            contentFit="cover"
                            alt={webImageAltGroupCover(displayName)}
                          />
                        ) : actorAvatarId || actorAvatarConfig ? (
                          <AvatarDisplay
                            avatarId={actorAvatarId}
                            avatarConfig={actorAvatarConfig}
                            size={40}
                            imageAlt={webImageAltAvatarLabel(displayName)}
                          />
                        ) : (
                          <View style={styles.avatarFallback}>
                            <Text style={styles.avatarFallbackText} numberOfLines={1}>
                              {displayName.charAt(0).toUpperCase() || '?'}
                            </Text>
                          </View>
                        )}
                      </View>
                      <View style={styles.textColumn}>
                        <View style={styles.topLine}>
                          <Text
                            style={[
                              styles.nameText,
                              isUnread ? styles.nameTextUnread : styles.nameTextRead,
                            ]}
                            numberOfLines={1}
                          >
                            {displayName}
                          </Text>
                          <Text
                            style={[
                              styles.actionText,
                              isUnread ? styles.actionTextUnread : styles.actionTextRead,
                            ]}
                            numberOfLines={1}
                          >
                            {actionLabel}
                          </Text>
                        </View>
                        {contextMessage ? (
                          <Text
                            style={[
                              styles.messageText,
                              isUnread ? styles.messageTextUnread : styles.messageTextRead,
                            ]}
                            numberOfLines={3}
                          >
                            {contextMessage}
                          </Text>
                        ) : null}
                      </View>
                      <View style={styles.timeWrapper}>
                        <Text style={styles.timeText}>{formatRelativeTime(item.createdAt)}</Text>
                      </View>
                    </View>
                  </Pressable>
                  {!isLast ? <View style={styles.itemSeparator} /> : null}
                </View>
              )
            })}
          </ScrollView>
          {!isAtBottom && (
            <LinearGradient
              colors={['transparent', theme.background.cardElevated]}
              style={styles.scrollFade}
              pointerEvents="none"
            />
          )}
          </View>
        )}
      </View>
    </View>
  )
}
